import { ptBR } from "date-fns/locale";
import {
  DateCalendar as DateCalendarMui,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { IconButton, Badge } from "@mui/material";
import { addMonths } from "date-fns";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { format } from "date-fns";
import { useMemo } from "react";
import { Typography } from "../../../components";

const DateCalendarCustom = ({
  value,
  onChange,
  onMonthChange,
  agendamentos = [],
  ...rest
}) => {
  const disabledPrevMonth = rest.disabledPrevMonth;
  const disabledNextMonth = rest.disabledNextMonth;
  const diasSemana = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const agendamentosMap = useMemo(() => {
    const map = new Map();
    agendamentos.forEach(({ dia, total }) => {
      map.set(dia, total);
    });
    return map;
  }, [agendamentos]);

  const dayFontSize = {
    xs: 15,
    md: 22,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <DateCalendarMui
        value={value}
        dayOfWeekFormatter={(_, date) => diasSemana[date.getDay()]}
        onChange={onChange}
        sx={{
          width: "100%",
          height: { xs: "100%", md: "400px" },
          maxHeight: "100%",
          "& .MuiDayCalendar-weekContainer": {
            justifyContent: "center",
            flex: 1,
          },
          "& .MuiBadge-root": {
            width: { xs: "10%", md: "7%" },
            height: {xs: "78%", md: "70%"},
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
          },
          "& .MuiPickersDay-root": {
            margin: "0 !important",
            width: "120%",
            height: {
              xs: "120%",
              md: "150%",
            },
            fontSize: dayFontSize,
            fontWeight: "600 !important",
            borderRadius: "20%",
          },
          "& .MuiPickersFadeTransitionGroup-root, & .MuiDayCalendar-root, & .MuiPickersFadeTransitionGroup-root > div, & .MuiPickersSlideTransition-root":
            {
              height: "98% !important",
            },
          "& .MuiDayCalendar-header > span": {
            fontWeight: "700 !important",
          },
          "& .MuiDayCalendar-monthContainer": {
            height: "100%",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiDayCalendar-weekDayLabel, & .MuiDayCalendar-day": {
            fontSize: dayFontSize,
            flex: 1,
            display: "flex",
            justifyContent: "center",
          },
        }}
        onMonthChange={onMonthChange}
        slots={{
          calendarHeader: (props) =>
            CustomCalendarHeader({
              ...props,
              disabledPrevMonth,
              disabledNextMonth,
            }),
          day: (props) => CustomDay({ ...props, agendamentosMap }),
        }}
        views={["year", "month", "day"]}
        {...rest}
      />
    </LocalizationProvider>
  );
};

const CustomDay = (props) => {
  const { day, outsideCurrentMonth, agendamentosMap, ...other } = props;
  const diaFormatado = format(day, "yyyy-MM-dd");
  const total = agendamentosMap.get(diaFormatado);

  return (
    <Badge
      overlap="circular"
      variant="dot"
      sx={{
        "& .MuiBadge-badge": {
          backgroundColor: other.selected ? "#fff" : "primary.main",
        },
      }}
      invisible={!total}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
      />
    </Badge>
  );
};

const CustomCalendarHeader = (props) => {
  const {
    currentMonth,
    onMonthChange,
    onViewChange,
    views,
    disabledPrevMonth,
    disabledNextMonth,
  } = props;

  const label = currentMonth
    .toLocaleDateString("pt-BR", {
      month: "long",
      year: "numeric",
    })
    .toUpperCase();

  const changeMonth = (diff) => {
    const newMonth = addMonths(currentMonth, diff);
    onMonthChange(newMonth, diff > 0 ? "left" : "right");
  };

  return (
    <div className="d-flex align-items-center justify-content-center w-100 gap-4 px-2 py-3 mb-1">
      <div style={{ flex: 1, height: 1, background: "#eeeeee" }} />
      <IconButton
        size="small"
        sx={{
          background: "#f7f7f7",
          ":hover": {
            background: "#e0f0ff",
          },
        }}
        disabled={disabledPrevMonth}
        onClick={() => changeMonth(-1)}
      >
        <i
          className="ph-bold ph-arrow-left"
          style={{
            color: disabledPrevMonth ? "#00000061" : "#1976d2",
          }}
        />
      </IconButton>
      <Typography
        component="span"
        onClick={() => {
          if (views?.includes("month")) {
            onViewChange("month");
          }
        }}
        sx={{
          fontSize: {
            xs: 18,
            md: 23,
          },
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <IconButton
        size="small"
        sx={{
          background: "#f7f7f7",
          ":hover": {
            background: "#e0f0ff",
          },
        }}
        color="primary"
        disabled={disabledNextMonth}
        onClick={() => changeMonth(1)}
      >
        <i
          className="ph-bold ph-arrow-right"
          style={{
            color: disabledNextMonth ? "#00000061" : "#1976d2",
          }}
        />
      </IconButton>

      <div style={{ flex: 1, height: 1, background: "#eeeeee" }} />
    </div>
  );
};

export default DateCalendarCustom;
